import React, {useRef} from "react";
import {useSelector} from "react-redux";
import {useLocation} from "react-router-dom";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {
    SETTINGS_NAVIGATION_DEFINITIONS,
    resolveSettingsRoute,
    type SettingsNavigationId,
} from "../settingsNavigation";

type SettingsRecentRoutesContextValue = {
    recentRouteIds: SettingsNavigationId[]
    removeRecentRoute: (routeId: SettingsNavigationId) => void
}

type RecentRoutesSnapshot = {
    storageKey: string | null
    routeIds: SettingsNavigationId[]
}

const STORAGE_KEY_PREFIX = 'crqlar:settings-recent-routes:v1'
const RECENT_ROUTE_LIMIT = 5
const EMPTY_ROUTE_IDS: SettingsNavigationId[] = []
const VALID_ROUTE_IDS = new Set<string>(SETTINGS_NAVIGATION_DEFINITIONS.map(route => route.id))
const SettingsRecentRoutesContext = React.createContext<SettingsRecentRoutesContextValue | undefined>(undefined)

const readRecentRouteIds = (storageKey: string) => {
    try {
        const storedValue = window.localStorage.getItem(storageKey)
        if (!storedValue) return EMPTY_ROUTE_IDS

        const parsedValue = JSON.parse(storedValue)
        if (!Array.isArray(parsedValue)) return EMPTY_ROUTE_IDS

        return Array.from(new Set(parsedValue.filter(
            (routeId): routeId is SettingsNavigationId => typeof routeId === 'string' && VALID_ROUTE_IDS.has(routeId),
        ))).slice(0, RECENT_ROUTE_LIMIT)
    } catch {
        return EMPTY_ROUTE_IDS
    }
}

const writeRecentRouteIds = (storageKey: string, routeIds: SettingsNavigationId[]) => {
    const nextRouteIds = Array.from(new Set(routeIds)).slice(0, RECENT_ROUTE_LIMIT)

    try {
        window.localStorage.setItem(storageKey, JSON.stringify(nextRouteIds))
    } catch {
        // Recent settings stay optional when storage is unavailable.
    }

    return nextRouteIds
}

const useRecentRoutesStorageKey = () => {
    const organization = useOrganization()
    const userId = useSelector<any, string | undefined>(state => state.auth.user?.id)

    return organization?.id && userId
        ? `${STORAGE_KEY_PREFIX}:${organization.id}:${userId}`
        : null
}

export const SettingsRecentRoutesProvider = ({children}: React.PropsWithChildren) => {
    const {pathname} = useLocation()
    const storageKey = useRecentRoutesStorageKey()
    const routeId = resolveSettingsRoute(pathname)?.id as SettingsNavigationId | undefined
    const trackedPathRef = useRef<string | null>(null)
    const [snapshot, setSnapshot] = React.useState<RecentRoutesSnapshot>(() => ({
        storageKey,
        routeIds: storageKey ? readRecentRouteIds(storageKey) : EMPTY_ROUTE_IDS,
    }))

    const recentRouteIds = snapshot.storageKey === storageKey
        ? snapshot.routeIds
        : storageKey ? readRecentRouteIds(storageKey) : EMPTY_ROUTE_IDS
    const recentRouteIdsRef = useRef(recentRouteIds)
    recentRouteIdsRef.current = recentRouteIds

    const updateRecentRoutes = React.useCallback((update: (routeIds: SettingsNavigationId[]) => SettingsNavigationId[]) => {
        if (!storageKey) return

        const nextRouteIds = writeRecentRouteIds(storageKey, update(recentRouteIdsRef.current))
        recentRouteIdsRef.current = nextRouteIds
        setSnapshot({storageKey, routeIds: nextRouteIds})
    }, [storageKey])

    React.useEffect(() => {
        setSnapshot({
            storageKey,
            routeIds: storageKey ? readRecentRouteIds(storageKey) : EMPTY_ROUTE_IDS,
        })
    }, [storageKey])

    React.useEffect(() => {
        if (!storageKey || !routeId) {
            trackedPathRef.current = null
            return
        }

        const normalizedPathname = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname
        const trackedPath = `${storageKey}:${normalizedPathname}`
        if (trackedPathRef.current === trackedPath) return

        trackedPathRef.current = trackedPath
        updateRecentRoutes(currentRouteIds => currentRouteIds[0] === routeId
            ? currentRouteIds
            : [routeId, ...currentRouteIds.filter(currentRouteId => currentRouteId !== routeId)])
    }, [pathname, routeId, storageKey, updateRecentRoutes])

    React.useEffect(() => {
        if (!storageKey) return

        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === storageKey || event.key === null) {
                setSnapshot({storageKey, routeIds: readRecentRouteIds(storageKey)})
            }
        }

        window.addEventListener('storage', handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [storageKey])

    const removeRecentRoute = React.useCallback((routeIdToRemove: SettingsNavigationId) => {
        updateRecentRoutes(currentRouteIds => currentRouteIds.filter(routeId => routeId !== routeIdToRemove))
    }, [updateRecentRoutes])

    const contextValue = React.useMemo<SettingsRecentRoutesContextValue>(() => ({
        recentRouteIds,
        removeRecentRoute,
    }), [recentRouteIds, removeRecentRoute])

    return (
        <SettingsRecentRoutesContext.Provider value={contextValue}>
            {children}
        </SettingsRecentRoutesContext.Provider>
    )
}

export const useSettingsRecentRoutes = () => {
    const context = React.useContext(SettingsRecentRoutesContext)
    if (!context) throw new Error('useSettingsRecentRoutes must be used within SettingsRecentRoutesProvider')
    return context
}
